const MAX_CACHE = 200

// Module-level elevation tile cache shared between TerrainOverlay and viewshed computation.
// Keys are "z/x/y", values are Float32Array (256×256) elevation grids in meters.
export const elevCache = new Map()

// Parallel cache of per-tile elevation extents { min, max } in meters, keyed identically
// to elevCache. Computed once when a grid is cached so the terrain panel can derive true
// viewport min/max from full-resolution data instead of sparse point sampling.
const elevStats = new Map()

// Subscribers notified (after each genuine tile decode) so the panel can re-derive extents
// once newly-revealed viewport tiles finish loading.
const cacheListeners = new Set()

// Subscribe to cache additions. Returns an unsubscribe function.
export function onCacheChange(fn) {
  cacheListeners.add(fn)
  return () => cacheListeners.delete(fn)
}

function tileExtents(grid) {
  let min = Infinity
  let max = -Infinity
  for (let i = 0; i < grid.length; i++) {
    const v = grid[i]
    if (v < min) min = v
    if (v > max) max = v
  }
  return { min, max }
}

export function cacheSet(key, value) {
  if (elevCache.size >= MAX_CACHE) {
    const oldest = elevCache.keys().next().value
    elevCache.delete(oldest)
    elevStats.delete(oldest)
  }
  elevCache.set(key, value)
  elevStats.set(key, tileExtents(value))
  for (const fn of cacheListeners) {
    try { fn(key) } catch (_) {}
  }
}

// Standard slippy-map tile indices for a lon/lat at zoom z.
function lonToTileX(lon, z) {
  return Math.floor(((lon + 180) / 360) * Math.pow(2, z))
}
function latToTileY(lat, z) {
  const r = (lat * Math.PI) / 180
  return Math.floor(
    ((1 - Math.log(Math.tan(r) + 1 / Math.cos(r)) / Math.PI) / 2) * Math.pow(2, z),
  )
}

// Derive true elevation extents (meters) over the cached tiles that cover the given
// viewport. bounds: { west, east, south, north } in degrees.
//
// The terrain overlay filters tiles at full per-pixel resolution, so the panel's
// elevation range must come from the same data. We aggregate each cached tile's
// precomputed min/max rather than re-scanning grids.
//
// The display zoom MapLibre renders at isn't known precisely here (raster tile-zoom
// selection depends on SDK internals), so instead of trusting a single computed zoom we
// score every zoom level present in the cache by how much of the viewport its cached
// tiles actually cover, and use the best-covered level. This self-corrects after
// zoom-out, when stale fine-zoom tiles linger in the cache but cover only a sliver.
//
// Returns { minM, maxM } or null if no cached tile covers the viewport.
export function viewportElevExtents(bounds) {
  const { west, east, south, north } = bounds

  // Group cached tiles by zoom: zoom -> Map<"x/y", {min,max}>
  const byZoom = new Map()
  for (const [key, stat] of elevStats) {
    const slash = key.indexOf('/')
    const z = parseInt(key.slice(0, slash), 10)
    let level = byZoom.get(z)
    if (!level) { level = new Map(); byZoom.set(z, level) }
    level.set(key.slice(slash + 1), stat)
  }

  let best = null // { coverage, z, minM, maxM }

  for (const [z, level] of byZoom) {
    const n = Math.pow(2, z)
    const clamp = v => Math.max(0, Math.min(n - 1, v))
    const xMin = clamp(lonToTileX(west, z))
    const xMax = clamp(lonToTileX(east, z))
    const yMin = clamp(latToTileY(north, z)) // north → smaller y
    const yMax = clamp(latToTileY(south, z))
    const needed = (xMax - xMin + 1) * (yMax - yMin + 1)
    if (needed <= 0) continue

    let count = 0
    let minM = Infinity
    let maxM = -Infinity
    for (const [xy, stat] of level) {
      const s = xy.indexOf('/')
      const x = parseInt(xy.slice(0, s), 10)
      const y = parseInt(xy.slice(s + 1), 10)
      if (x < xMin || x > xMax || y < yMin || y > yMax) continue
      count++
      if (stat.min < minM) minM = stat.min
      if (stat.max > maxM) maxM = stat.max
    }
    if (count === 0) continue

    const coverage = count / needed
    // Prefer the level covering the most of the viewport; break ties toward finer zoom.
    if (!best || coverage > best.coverage || (coverage === best.coverage && z > best.z)) {
      best = { coverage, z, minM, maxM }
    }
  }

  return best ? { minM: best.minM, maxM: best.maxM } : null
}

export async function fetchElevTile(z, tx, ty, signal) {
  const key = `${z}/${tx}/${ty}`
  const cached = elevCache.get(key)
  if (cached) return cached

  const url = `https://elevation-tiles-prod.s3.amazonaws.com/terrarium/${z}/${tx}/${ty}.png`
  const res = await fetch(url, signal ? { signal } : undefined)
  if (!res.ok) throw new Error(`terrarium ${z}/${tx}/${ty}: ${res.status}`)

  const blob = await res.blob()
  let bitmap
  try {
    bitmap = await createImageBitmap(blob, { colorSpaceConversion: 'none' })
  } catch (_) {
    bitmap = await createImageBitmap(blob)
  }

  const canvas = new OffscreenCanvas(256, 256)
  const ctx = canvas.getContext('2d', { willReadFrequently: true })
  ctx.drawImage(bitmap, 0, 0)
  const { data } = ctx.getImageData(0, 0, 256, 256)

  const elev = new Float32Array(256 * 256)
  for (let i = 0; i < 256 * 256; i++) {
    const b = i * 4
    elev[i] = (data[b] * 256 + data[b + 1] + data[b + 2] / 256) - 32768
  }

  cacheSet(key, elev)
  return elev
}
